import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MapPin, User } from "lucide-react";
import type { PublicBookletResponseDto } from "@tabipla/shared";

interface PublicBookletCardProps {
  booklet: PublicBookletResponseDto;
}

// Color themes for variety - cycle through based on index
const colorThemes = [
  {
    gradientStyle: "linear-gradient(135deg, #059669 0%, #0d9488 50%, #0891b2 100%)",
    decorColor1: "rgba(52, 211, 153, 0.4)",
    decorColor2: "rgba(94, 234, 212, 0.3)",
  },
  {
    gradientStyle: "linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #d946ef 100%)",
    decorColor1: "rgba(167, 139, 250, 0.4)",
    decorColor2: "rgba(216, 180, 254, 0.3)",
  },
  {
    gradientStyle: "linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #06b6d4 100%)",
    decorColor1: "rgba(96, 165, 250, 0.4)",
    decorColor2: "rgba(147, 197, 253, 0.3)",
  },
  {
    gradientStyle: "linear-gradient(135deg, #dc2626 0%, #f97316 50%, #eab308 100%)",
    decorColor1: "rgba(251, 146, 60, 0.4)",
    decorColor2: "rgba(253, 224, 71, 0.3)",
  },
];

// Simple hash function to get consistent color for each booklet
function getColorIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % colorThemes.length;
}

export function PublicBookletCard({ booklet }: PublicBookletCardProps) {
  const { t } = useTranslation();
  const theme = colorThemes[getColorIndex(booklet.id)];

  return (
    <Link
      to={`/booklets/public/${booklet.id}`}
      className="group block cursor-pointer overflow-hidden rounded-2xl bg-card shadow-md ring-1 ring-black/5 transition-all hover:shadow-xl hover:ring-black/10 active:scale-[0.98]"
    >
      {/* Cover Image */}
      <div className="relative h-32 overflow-hidden sm:h-40">
        {booklet.coverImageUrl ? (
          <img
            src={booklet.coverImageUrl}
            alt={booklet.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          /* Decorative placeholder */
          <div className="relative h-full w-full" style={{ background: theme.gradientStyle }}>
            {/* Decorative circles */}
            <div
              className="absolute -right-6 -top-6 h-24 w-24 rounded-full"
              style={{ backgroundColor: theme.decorColor1 }}
            />
            <div
              className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full"
              style={{ backgroundColor: theme.decorColor2 }}
            />
            {/* Spot count indicator */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-0.5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                <span className="text-3xl font-extrabold">{booklet.spotCount}</span>
                <span className="text-xs font-bold uppercase tracking-widest">spots</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Author */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {booklet.author.image ? (
            <img
              src={booklet.author.image}
              alt={booklet.author.name}
              className="h-6 w-6 rounded-full object-cover"
            />
          ) : booklet.author.avatarEmoji && booklet.author.avatarColor ? (
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${booklet.author.avatarColor} text-xs`}
            >
              {booklet.author.avatarEmoji}
            </div>
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
          )}
          <span className="truncate">{booklet.author.name}</span>
        </div>

        {/* Title */}
        <h3 className="mt-2 line-clamp-2 font-bold leading-snug transition-colors group-hover:text-primary">
          {booklet.title}
        </h3>

        {booklet.description && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{booklet.description}</p>
        )}

        {/* Spot count (only if cover image exists) */}
        {booklet.coverImageUrl && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            {booklet.spotCount > 0
              ? t("booklets.spots", { count: booklet.spotCount })
              : t("booklets.noSpots")}
          </div>
        )}
      </div>
    </Link>
  );
}
